import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import CountrySelect from "../components/CountrySelect";
import { ImagePlus, X, Lock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AnnonseEditor() {
  const { id } = useParams();
  const editing = !!id;
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [cats, setCats] = useState({ categories: [], can_post: false });
  const [f, setF] = useState({
    category: "", subcategory: "",
    title: "", body: "",
    country: "NO", fylke: "", city: "",
    price: "",
    tags: "",
    image_paths: [],
    valid_days: 30,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [locations, setLocations] = useState({ countries: [], top_countries: ["NO","SE","DK","GB","US"], subdivisions: {}, fylker: [], cities_by_fylke: {} });

  useEffect(() => {
    api.get("/ads/categories").then((r) => setCats(r.data));
    api.get("/search/locations").then((r) => setLocations(r.data)).catch(() => {});
    if (editing) {
      api.get(`/ads/${id}`).then((r) => {
        const a = r.data;
        setF({
          category: a.category || "", subcategory: a.subcategory || "",
          title: a.title, body: a.body,
          country: a.country || "NO", fylke: a.fylke || "", city: a.city || "",
          price: a.price ?? "",
          tags: (a.tags || []).join(", "),
          image_paths: a.image_paths || [],
          valid_days: 30,
        });
      }).catch((e) => setError(formatApiError(e)));
    }
  }, [id, editing]);

  const cat = cats.categories.find((c) => c.key === f.category);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newPaths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post("/ads/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(r.data.path);
      }
      set("image_paths", [...f.image_paths, ...newPaths].slice(0, 8));
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (p) => set("image_paths", f.image_paths.filter((x) => x !== p));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const payload = {
        category: f.category, subcategory: f.subcategory,
        title: f.title, body: f.body,
        country: f.country || "NO",
        fylke: f.fylke || null, city: f.city || null,
        price: f.price === "" ? null : parseInt(f.price),
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        image_paths: f.image_paths,
        valid_days: f.valid_days,
      };
      if (editing) {
        await api.patch(`/ads/${id}`, payload);
        nav(`/annonser/se/${id}`);
      } else {
        const r = await api.post("/ads", payload);
        nav(`/annonser/se/${r.data.id}`);
      }
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (!editing && !cats.can_post && cats.categories.length > 0) {
    return (
      <Layout withSidebar={false}>
        <div className="max-w-xl mx-auto text-center py-16">
          <Lock className="h-10 w-10 text-[var(--cognac)] mx-auto mb-4" />
          <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Krever VIP eller verifisering</h1>
          <p className="font-manrope text-[var(--cream-soft)] mb-6 text-sm leading-relaxed">
            Kun verifiserte medlemmer og VIP/Creator kan publisere annonser. Dette hindrer spam og falske annonser.
          </p>
          <Link to="/oppgrader" className="btn-magenta">Bli VIP</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout withSidebar={false}>
      <h1 className="font-fraunces italic text-4xl text-[var(--cream)] leading-tight mb-6">
        {editing ? "Rediger annonse." : "Ny annonse."}
      </h1>

      <form onSubmit={submit} className="max-w-3xl space-y-5" data-testid="ad-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="font-meta mb-2">Hovedkategori</div>
            <select required value={f.category} onChange={(e) => { set("category", e.target.value); set("subcategory", ""); }}
                    className="input-editorial w-full" data-testid="cat-select">
              <option value="">Velg …</option>
              {cats.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div className="font-meta mb-2">Underkategori</div>
            <select required value={f.subcategory} onChange={(e) => set("subcategory", e.target.value)}
                    disabled={!cat} className="input-editorial w-full disabled:opacity-50" data-testid="sub-select">
              <option value="">{cat ? "Velg …" : "Velg hovedkategori først"}</option>
              {cat?.subs.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <div className="font-meta mb-2">Tittel</div>
          <input required minLength={3} maxLength={120} value={f.title} onChange={(e) => set("title", e.target.value)}
                 className="input-editorial w-full" data-testid="title-input" />
        </div>

        <div>
          <div className="font-meta mb-2">Beskrivelse</div>
          <textarea required minLength={10} maxLength={4000} rows={8}
                    value={f.body} onChange={(e) => set("body", e.target.value)}
                    className="input-editorial w-full" data-testid="body-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="font-meta mb-2">Land</div>
            <CountrySelect
              value={f.country}
              onChange={(iso) => { set("country", iso || "NO"); set("fylke", ""); }}
              allCountries={locations.countries || []}
              topCountries={locations.top_countries || ["NO","SE","DK","GB","US"]}
              allowEmpty={false}
              testIdPrefix="ad-country"
            />
          </div>
          <div>
            <div className="font-meta mb-2">
              {f.country === "SE" ? "Län"
                : f.country === "DK" ? "Region"
                : f.country === "NO" ? "Fylke"
                : "Region"}
            </div>
            <select value={f.fylke} onChange={(e) => { set("fylke", e.target.value); set("city", ""); }}
                    className="input-editorial w-full" data-testid="ad-fylke">
              <option value="">—</option>
              {((f.country && locations.subdivisions?.[f.country]) || (f.country === "NO" ? (locations.fylker || []) : [])).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <div className="font-meta mb-2">By</div>
            <input value={f.city} onChange={(e) => set("city", e.target.value)} className="input-editorial w-full" data-testid="ad-city" />
          </div>
          <div>
            <div className="font-meta mb-2">Pris (kr) — valgfritt</div>
            <input type="number" min="0" value={f.price} onChange={(e) => set("price", e.target.value)}
                   className="input-editorial w-full" />
          </div>
        </div>

        <div>
          <div className="font-meta mb-2">Tagger (komma-separert)</div>
          <input value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="oslo, kveld, sosial"
                 className="input-editorial w-full" />
        </div>

        <div>
          <div className="font-meta mb-2">Bilder ({f.image_paths.length} / 8)</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {f.image_paths.map((p) => (
              <div key={p} className="relative">
                <img src={`${BACKEND}/api/ads/images/raw?path=${encodeURIComponent(p)}`}
                     alt="" className="w-24 h-24 object-cover border border-white/15" />
                <button type="button" onClick={() => removeImage(p)}
                        className="absolute -top-2 -right-2 p-1 bg-[var(--noir)] border border-white/15 rounded-full hover:text-[var(--magenta)]" aria-label="Fjern">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || f.image_paths.length >= 8}
                  className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="upload-image-btn">
            <ImagePlus className="h-3.5 w-3.5" />
            {uploading ? "Laster opp …" : "Legg til bilde"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadFiles} />
        </div>

        {error && <div className="text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3 text-sm">{error}</div>}

        <div className="flex items-center gap-3 pt-3 border-t border-white/10">
          <button type="submit" disabled={saving} className="btn-magenta" data-testid="save-btn">
            {saving ? "Lagrer …" : editing ? "Lagre endringer" : "Publiser"}
          </button>
          <Link to="/annonser" className="btn-ghost text-xs">Avbryt</Link>
        </div>
      </form>
    </Layout>
  );
}
